import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { USER_ROLES } from '../../common/constants';
import { useAuthStore } from '../../store/authStore';
import { useUIStore } from '../../store/uiStore';

interface Command {
    label: string;
    hint?: string;
    keywords: string;
    path: string;
    roles?: string[];
}

const COMMANDS: Command[] = [
    { label: 'Go to Dashboard', keywords: 'home overview', path: '/dashboard' },
    { label: 'Browse Exams', keywords: 'exam list assessments tests', path: '/exams' },
    { label: 'Create Exam', hint: 'New assessment', keywords: 'new exam create test', path: '/exams/create' },
    { label: 'Question Bank', keywords: 'questions bank library', path: '/questions' },
    { label: 'Create Question', hint: 'Add to the bank', keywords: 'new question create add', path: '/questions/create' },
    {
        label: 'Pending Approvals',
        keywords: 'review approve pending moderation',
        path: '/questions/pending',
        roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_MODERATOR],
    },
    {
        label: 'Reported Issues',
        keywords: 'flags reports problems issues',
        path: '/questions/flags',
        roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_MODERATOR],
    },
    {
        label: 'Live Quiz',
        keywords: 'kahoot live quiz host session',
        path: '/live-quiz',
        roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_INSTRUCTOR],
    },
    {
        label: 'Analytics',
        keywords: 'reports charts insights statistics',
        path: '/analytics',
        roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_INSTRUCTOR],
    },
    { label: 'My Certificates', keywords: 'certificates awards achievements', path: '/certificates' },
    {
        label: 'Categories',
        keywords: 'categories taxonomy topics',
        path: '/categories',
        roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_INSTRUCTOR],
    },
    {
        label: 'Users',
        keywords: 'users members team people',
        path: '/users',
        roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_MODERATOR],
    },
    {
        label: 'Organizations',
        keywords: 'organizations tenants companies',
        path: '/organizations',
        roles: [USER_ROLES.SUPER_ADMIN],
    },
    { label: 'Profile', keywords: 'profile account me', path: '/profile' },
    { label: 'Settings', keywords: 'settings preferences theme password', path: '/settings' },
];

/** Ctrl+K / Cmd+K quick navigation. Mounted once inside the authenticated layout. */
const CommandPalette: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const { commandPaletteOpen: open, setCommandPaletteOpen } = useUIStore();
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    const available = useMemo(
        () => COMMANDS.filter((cmd) => !cmd.roles || (user?.role && cmd.roles.includes(user.role))),
        [user?.role],
    );

    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return available;
        return available.filter((cmd) => `${cmd.label} ${cmd.keywords}`.toLowerCase().includes(q));
    }, [available, query]);

    const close = useCallback(() => {
        setCommandPaletteOpen(false);
        setQuery('');
        setActiveIndex(0);
    }, [setCommandPaletteOpen]);

    // Global shortcut
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                const { commandPaletteOpen, setCommandPaletteOpen: setOpen } = useUIStore.getState();
                setOpen(!commandPaletteOpen);
            } else if (e.key === 'Escape') {
                close();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [close]);

    useEffect(() => {
        if (open) {
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    }, [open]);

    useEffect(() => {
        setActiveIndex(0);
    }, [query]);

    if (!open) return null;

    const run = (command: Command) => {
        close();
        navigate(command.path);
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] px-4" role="dialog" aria-modal="true">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={close} />

            {/* Panel */}
            <div className="relative w-full max-w-lg bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden">
                <input
                    ref={inputRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            setActiveIndex((i) => Math.min(results.length - 1, i + 1));
                        } else if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            setActiveIndex((i) => Math.max(0, i - 1));
                        } else if (e.key === 'Enter' && results[activeIndex]) {
                            run(results[activeIndex]);
                        }
                    }}
                    placeholder="Type a page or action..."
                    className="w-full px-4 py-3.5 text-sm bg-transparent text-slate-900 dark:text-white placeholder-slate-400 outline-none border-b border-slate-200 dark:border-slate-700"
                />
                <div className="max-h-72 overflow-y-auto py-1.5">
                    {results.length === 0 ? (
                        <div className="px-4 py-6 text-center text-sm text-slate-400 dark:text-slate-500">No matches</div>
                    ) : (
                        results.map((command, index) => (
                            <button
                                key={command.path + command.label}
                                onClick={() => run(command)}
                                onMouseEnter={() => setActiveIndex(index)}
                                className={`w-full flex items-center justify-between px-4 py-2.5 text-left text-sm transition-colors ${
                                    index === activeIndex
                                        ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300'
                                        : 'text-slate-700 dark:text-slate-300'
                                }`}
                            >
                                <span className="font-medium">{command.label}</span>
                                {command.hint && <span className="text-xs text-slate-400 dark:text-slate-500">{command.hint}</span>}
                            </button>
                        ))
                    )}
                </div>
                <div className="px-4 py-2 border-t border-slate-200 dark:border-slate-700 text-[11px] text-slate-400 dark:text-slate-500 flex gap-4">
                    <span>↑↓ Navigate</span>
                    <span>↵ Open</span>
                    <span>Esc Close</span>
                </div>
            </div>
        </div>
    );
};

export default CommandPalette;
